import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  Select,
  Input,
  Table,
  Checkbox,
  Spin,
  Alert,
  message,
  Modal,
  Tooltip
} from 'antd';
import styled from 'styled-components';
import InfoModal from '../common/InfoModal';
import { filterAlertStatusFromWorkflowStatus } from '../utils/alerts';
import {
  bulkUpdateAlertStatus,
  bulkUpdateAlertStatusReset
} from '../../../redux/actions/cm/alertAction';
// FIN-10457: Import selectAlertsId to clear selection after bulk update
import {
  fetchAlerts,
  selectAlertsId
} from '../../../redux/actions/cm/alertList';
import { AlertDetailsType, UserType } from '../propTypes';
import { getCurrentUser } from '../../../redux/selectors/cm/users';
// FIN-11261: Terminal alert statuses Investigators cannot bulk-update
import {
  ALERT_CLOSURE_STATUSES,
  MAX_BULK_STATUS_UPDATE_ALERTS
} from '../../constants/cmConstants';

const { TextArea } = Input;
const { Option } = Select;

export const BulkUpdateAlertStatusModal = ({
  visible,
  handleExit,
  selectedIds,
  selectedAlerts,
  workFlowStatusList,
  dispatch,
  bulkUpdate,
  alertListFilters,
  alertListCurrentPage,
  alertListPageSize,
  currentUser,
  confirmBeforeSubmit,
  titleOverride,
  unassignedOnlyWarning,
  onBulkSuccess,
  statusWhitelist
}) => {
  const [selectedStatus, setSelectedStatus] = useState(undefined);
  const [reason, setReason] = useState('');
  const [checkedAlertIds, setCheckedAlertIds] = useState([]);
  const [statusError, setStatusError] = useState('');
  const [reasonError, setReasonError] = useState('');
  // G-e: inline confirm state (only used when confirmBeforeSubmit=true)
  const [confirmOpen, setConfirmOpen] = useState(false);

  // FIN-11261: Investigator-only users cannot bulk-update terminal-status
  // alerts. Detected here (not at the listing) so users see exactly which of
  // their selected alerts were excluded and why.
  const isInvestigatorOnly =
    currentUser?.roles?.some((r) =>
      ['INVESTIGATOR', 'INVESTIGATOR_PLUS', 'INVESTIGATOR_CROSS'].includes(r)
    ) &&
    !currentUser?.roles?.some((r) =>
      ['SUPERVISOR', 'ADMIN', 'CHECKER'].includes(r)
    );

  const isClosedForInvestigator = (statusCode) =>
    isInvestigatorOnly && ALERT_CLOSURE_STATUSES.includes(statusCode);

  useEffect(() => {
    if (visible) {
      // FIN-11261: Pre-check only the alerts the Investigator is allowed to
      // update (terminal-status rows show as unchecked + disabled).
      setCheckedAlertIds(
        selectedAlerts
          .filter(
            ({ alert, metadata }) =>
              selectedIds.includes(alert?.id) &&
              !isClosedForInvestigator(metadata?.status)
          )
          .map(({ alert }) => alert.id)
      );
      setSelectedStatus(undefined);
      setReason('');
      setStatusError('');
      setReasonError('');
      dispatch(bulkUpdateAlertStatusReset());
    }
  }, [visible, selectedIds]);

  useEffect(() => {
    if (bulkUpdate.result) {
      const { totalUpdated, totalFailed, failures = [] } = bulkUpdate.result;

      if (onBulkSuccess) {
        // FIN-12030 (D8): FRAML path — delegate selection-clear and queue-refetch
        // to the caller; skip CM-specific selectAlertsId + fetchAlerts.
        onBulkSuccess(bulkUpdate.result);
      } else {
        // CM path (unchanged): clear selection, close, reset, refetch alert list.
        // FIN-10457: Clear selection to prevent stale data on modal reopen
        // FIN-10959: Clearing here (before the failure popup) still satisfies the
        // "clear selection post modal close" UX — the listing refetch just below
        // will drop the selected-alerts state; the failure popup lives in its
        // own antd Modal overlay and closes independently via its OK button.
        dispatch(selectAlertsId([]));
        dispatch(
          fetchAlerts({
            filters: alertListFilters,
            currentPage: alertListCurrentPage,
            pageSize: alertListPageSize
          })
        );
      }
      handleExit();
      dispatch(bulkUpdateAlertStatusReset());

      if (totalFailed === 0) {
        message.success(
          'Bulk update completed. The status of the selected alerts has been updated.'
        );
      } else {
        // FIN-10959: On partial or full failure, surface the per-alert
        // failures (alert ID + reason) via a persistent antd Modal instead of
        // a transient toast — the user needs to see which alerts failed and
        // why before they can act on it.
        const title =
          totalUpdated > 0
            ? `Bulk update completed with ${totalFailed} failure${
                totalFailed === 1 ? '' : 's'
              }`
            : 'Bulk update could not be completed';
        const summary =
          totalUpdated > 0
            ? `${totalFailed} of ${
                totalUpdated + totalFailed
              } alerts could not be updated:`
            : `${totalFailed} alert${
                totalFailed === 1 ? '' : 's'
              } could not be updated:`;
        const modalFn = totalUpdated > 0 ? Modal.warning : Modal.error;
        modalFn({
          title,
          okText: 'Close',
          width: 560,
          content: (
            <div>
              <p style={{ marginBottom: 8 }}>{summary}</p>
              <ul
                style={{
                  paddingLeft: 20,
                  margin: 0,
                  maxHeight: 280,
                  overflowY: 'auto'
                }}>
                {failures.map((f) => (
                  <li key={f.alertId} style={{ marginBottom: 4 }}>
                    <strong>{f.alertId}</strong>
                    {f.reason ? ` — ${f.reason}` : null}
                  </li>
                ))}
              </ul>
            </div>
          )
        });
      }
    }
  }, [bulkUpdate.result]);

  const statusOptions = useMemo(() => {
    // FIN-10750: Use filterAlertStatusFromWorkflowStatus to remove statuses not valid for alerts
    //            (ON_HOLD, ISSUE_NOT_STR, ISSUE_REPORT_STR)
    // FIN-9812: Also remove ASSIGNED (bulk assignment exists separately)
    const base = (
      filterAlertStatusFromWorkflowStatus(workFlowStatusList) || []
    ).filter((status) => status.code !== 'ASSIGNED');
    // FIN-11360 (A2): FRAML View restricts the dropdown to a whitelist (No Issue +
    // Hibernate) via opt-in prop; CM callers pass nothing → full list unchanged.
    return statusWhitelist && statusWhitelist.length > 0
      ? base.filter((status) => statusWhitelist.includes(status.code))
      : base;
  }, [workFlowStatusList, statusWhitelist]);

  const getStatusDisplayName = (statusCode) => {
    const status = workFlowStatusList?.find((s) => s.code === statusCode);
    return status?.currentValue || status?.defaultValue || statusCode || 'N/A';
  };

  const alertTableData = useMemo(() => {
    return selectedAlerts
      .filter(({ alert }) => selectedIds.includes(alert?.id))
      .map(({ alert, metadata }) => ({
        key: alert.id,
        alertId: alert.id,
        // FIN-11261: Keep raw status for Investigator gating
        statusCode: metadata?.status,
        currentStatus: getStatusDisplayName(metadata?.status)
      }));
  }, [selectedAlerts, selectedIds, workFlowStatusList]);

  // FIN-11261: Rows the current user is permitted to bulk-update
  const selectableAlerts = alertTableData.filter(
    (row) => !isClosedForInvestigator(row.statusCode)
  );

  const isAllChecked =
    selectableAlerts.length > 0 &&
    checkedAlertIds.length === selectableAlerts.length;

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setCheckedAlertIds(selectableAlerts.map((row) => row.alertId));
    } else {
      setCheckedAlertIds([]);
    }
  };

  const handleCheckAlert = (alertId, checked) => {
    if (checked) {
      setCheckedAlertIds((prev) => [...prev, alertId]);
    } else {
      setCheckedAlertIds((prev) => prev.filter((id) => id !== alertId));
    }
  };

  const columns = [
    {
      title: (
        <Checkbox
          checked={isAllChecked}
          indeterminate={
            checkedAlertIds.length > 0 &&
            checkedAlertIds.length < selectableAlerts.length
          }
          onChange={handleSelectAll}
          disabled={selectableAlerts.length === 0}
        />
      ),
      dataIndex: 'checkbox',
      width: 50,
      // FIN-11261: Disable + uncheck terminal-status rows for Investigators
      // FIN-11272: All three terminal statuses (HIBERNATE / AUTO_HIBERNATE /
      // NO_ISSUE) are treated as "Closed" per product, so use a single tooltip.
      render: (_, record) => {
        const isDisabled = isClosedForInvestigator(record.statusCode);
        const checkbox = (
          <Checkbox
            checked={!isDisabled && checkedAlertIds.includes(record.alertId)}
            onChange={(e) => handleCheckAlert(record.alertId, e.target.checked)}
            disabled={isDisabled}
          />
        );
        if (!isDisabled) return checkbox;
        return (
          <Tooltip title={'Investigators cannot bulk-update Closed alerts.'}>
            {checkbox}
          </Tooltip>
        );
      }
    },
    {
      title: 'Alert ID',
      dataIndex: 'alertId',
      ellipsis: true
    },
    {
      title: 'Current Alert Disposition Status',
      dataIndex: 'currentStatus'
    }
  ];

  const doDispatchBulkUpdate = () => {
    setConfirmOpen(false);
    dispatch(
      bulkUpdateAlertStatus({
        alertIds: checkedAlertIds,
        status: selectedStatus,
        reason: reason.trim()
      })
    );
    // FIN-12030 (D8): Removed optimistic close. Both CM and FRAML paths now
    // close via the result effect (bulkUpdate.result triggers handleExit +
    // reset). This ensures FRAML sees success/error feedback before the modal
    // closes and allows onBulkSuccess to run the FRAML-specific lifecycle.
  };

  const handleSubmit = () => {
    let hasError = false;
    if (!selectedStatus) {
      setStatusError('Please select a new alert status.');
      hasError = true;
    }
    if (!reason.trim()) {
      setReasonError('Reason is required for bulk status updates.');
      hasError = true;
    }
    if (checkedAlertIds.length === 0) {
      message.error('Please select at least one alert.');
      hasError = true;
    }
    if (hasError) return;

    if (confirmBeforeSubmit) {
      setConfirmOpen(true);
    } else {
      doDispatchBulkUpdate();
    }
  };

  // FIN-13957 — same cap + hover note as BulkAlertStatusInCaseModal, so the 400 is never reached.
  const exceedsBulkLimit =
    checkedAlertIds.length > MAX_BULK_STATUS_UPDATE_ALERTS;

  const modalButtons = useMemo(
    () => ({
      primary: {
        label: 'Update Status',
        onClick: handleSubmit,
        isDisabled:
          !selectedStatus ||
          !reason.trim() ||
          checkedAlertIds.length === 0 ||
          exceedsBulkLimit ||
          bulkUpdate.isLoading,
        tooltip: exceedsBulkLimit
          ? `This feature supports ${MAX_BULK_STATUS_UPDATE_ALERTS} alerts only. Currently ${checkedAlertIds.length} selected.`
          : undefined
      },
      secondary: {
        label: 'Cancel',
        onClick: handleExit
      }
    }),
    [
      selectedStatus,
      reason,
      checkedAlertIds,
      exceedsBulkLimit,
      bulkUpdate.isLoading
    ]
  );

  return (
    <InfoModal
      isOpen={visible}
      buttons={modalButtons}
      title={titleOverride || 'Bulk Update Alert Status'}
      showCheckIcon={false}
      showTopButtons
      width="65%"
      closable
      onCancel={handleExit}>
      <Spin spinning={bulkUpdate.isLoading}>
        {bulkUpdate.hasError && (
          <Alert
            type="error"
            message={bulkUpdate.errorMessage}
            style={{ marginBottom: 16 }}
          />
        )}
        {unassignedOnlyWarning && (
          <DisclaimerText>
            This update will only impact unassigned alerts. Alerts already assigned to an investigator will be skipped.
          </DisclaimerText>
        )}
        <FormSection>
          <Label>
            New Alert Status <RequiredMark>*</RequiredMark>
          </Label>
          <Select
            placeholder="Select status"
            value={selectedStatus}
            onChange={(val) => {
              setSelectedStatus(val);
              setStatusError('');
            }}
            status={statusError ? 'error' : undefined}
            style={{ width: '100%' }}>
            {statusOptions.map((status) => (
              <Option key={status.code} value={status.code}>
                {status.currentValue || status.defaultValue}
              </Option>
            ))}
          </Select>
          {statusError && <ErrorText>{statusError}</ErrorText>}
        </FormSection>

        <FormSection>
          <Label>
            Reason for status update <RequiredMark>*</RequiredMark>
          </Label>
          <TextArea
            placeholder="Enter the reason for updating the selected alerts"
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setReasonError('');
            }}
            status={reasonError ? 'error' : undefined}
            rows={4}
            maxLength={5000}
            showCount
          />
          {reasonError && <ErrorText>{reasonError}</ErrorText>}
        </FormSection>

        <DisclaimerText>
          This action will update the status of all selected alerts on this page
          to the chosen status, irrespective of their current workflow step.
          Alerts on other pages are not affected.
        </DisclaimerText>

        <Table
          columns={columns}
          dataSource={alertTableData}
          pagination={false}
          size="small"
          scroll={{ y: 300 }}
        />

        <FooterText>
          {checkedAlertIds.length} alert
          {checkedAlertIds.length !== 1 ? 's' : ''} selected for update
        </FooterText>
      </Spin>
      {/* G-e: inline confirm dialog — only rendered when confirmBeforeSubmit=true */}
      {confirmBeforeSubmit && (
        <Modal
          visible={confirmOpen}
          title="Confirm bulk status update"
          onOk={doDispatchBulkUpdate}
          onCancel={() => setConfirmOpen(false)}
          okText="OK"
          cancelText="Cancel">
          Update {checkedAlertIds.length} alert
          {checkedAlertIds.length !== 1 ? 's' : ''} status?
        </Modal>
      )}
    </InfoModal>
  );
};

// FIN-10449: Required field indicator
const RequiredMark = styled.span`
  color: #ff4d4f;
`;

// FIN-10448: Extra bottom margin to prevent character count overlapping disclaimer
const FormSection = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.div`
  margin-bottom: 6px;
  font-weight: 500;
`;

const ErrorText = styled.div`
  color: #ff4d4f;
  font-size: 13px;
  margin-top: 4px;
`;

const DisclaimerText = styled.div`
  margin-bottom: 16px;
  padding: 8px 12px;
  background-color: #fff7e6;
  border: 1px solid #ffe58f;
  border-radius: 4px;
  font-size: 13px;
  color: #ad6800;
`;

const FooterText = styled.div`
  margin-top: 12px;
  font-weight: 500;
  color: #595959;
`;

BulkUpdateAlertStatusModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  handleExit: PropTypes.func.isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedAlerts: PropTypes.arrayOf(AlertDetailsType).isRequired,
  workFlowStatusList: PropTypes.array.isRequired,
  dispatch: PropTypes.func.isRequired,
  bulkUpdate: PropTypes.shape({
    isLoading: PropTypes.bool,
    hasError: PropTypes.bool,
    errorMessage: PropTypes.string,
    result: PropTypes.object
  }),
  alertListFilters: PropTypes.object,
  alertListCurrentPage: PropTypes.number,
  alertListPageSize: PropTypes.number,
  currentUser: UserType,
  confirmBeforeSubmit: PropTypes.bool,
  titleOverride: PropTypes.string,
  unassignedOnlyWarning: PropTypes.bool,
  // FIN-12030 (D8): FRAML-specific success callback. When provided, the result
  // effect calls this instead of selectAlertsId + fetchAlerts. Receives the
  // bulk-update result object. CM path leaves this undefined.
  onBulkSuccess: PropTypes.func
};

BulkUpdateAlertStatusModal.defaultProps = {
  confirmBeforeSubmit: false,
  titleOverride: undefined,
  unassignedOnlyWarning: false,
  onBulkSuccess: undefined
};

const mapStateToProps = (state) => ({
  bulkUpdate: state.cmAlertAction?.bulkUpdate || {
    isLoading: false,
    hasError: false,
    errorMessage: null,
    result: null
  },
  alertListFilters: state.cmAlertList?.filters || {},
  alertListCurrentPage: state.cmAlertList?.currentPage || 1,
  alertListPageSize: state.cmAlertList?.pageSize || 10,
  currentUser: getCurrentUser(state)
});

export default connect(mapStateToProps)(BulkUpdateAlertStatusModal);
